import { useCallback } from 'react';
import { useToast, type ToastOptions } from '@waymail/ui';
import { useAcaoEmMassa, useDesfazer, type AcaoEmMassa } from './consultas';
import type { OpcoesDeTriagem } from './triar';

/*
 * A porta de entrada da triagem (barra de selecao e menu de contexto). O
 * texto dos toasts, o Desfazer e a falha parcial moram em `triar.ts`, que so
 * carrega na primeira acao: o bundle inicial tem teto (Secao 14.1) e ninguem
 * le "3 mensagens arquivadas" antes de arquivar.
 */
/** O Desfazer do ultimo aviso ainda aberto: Ctrl+Z o dispara (N2). */
let ultimoDesfazer: { fn: () => void; expira: number } | null = null;

/** Espelha a duracao padrao do Toast (8 s). */
const DURACAO_DO_AVISO_MS = 8000;

/** Dispara o Desfazer pendente, uma vez. Devolve se havia algum. */
export function desfazerUltimo(agora = Date.now()): boolean {
  const pendente = ultimoDesfazer;
  ultimoDesfazer = null;
  if (pendente === null || pendente.expira < agora) return false;
  pendente.fn();
  return true;
}

/** Lembra o Desfazer de cada aviso; um aviso sem ele (ex.: "Desfeito.") apaga o lembrado. */
export function lembrandoDesfazer(toast: (o: ToastOptions) => void) {
  return (opcoes: ToastOptions) => {
    ultimoDesfazer =
      opcoes.action?.label === 'Desfazer'
        ? {
            fn: opcoes.action.onAction,
            expira: Date.now() + (opcoes.duration ?? DURACAO_DO_AVISO_MS),
          }
        : null;
    toast(opcoes);
  };
}

export function useTriagem() {
  const { mutateAsync } = useAcaoEmMassa();
  const { mutateAsync: desfazer } = useDesfazer();
  const toast = useToast();

  const executar = useCallback(
    (ids: string[], action: AcaoEmMassa, opcoes?: OpcoesDeTriagem) =>
      import('./triar').then((m) =>
        m.triar({ mutateAsync, desfazer, toast: lembrandoDesfazer(toast) }, ids, action, opcoes),
      ),
    [mutateAsync, desfazer, toast],
  );

  return { executar };
}
